import React from 'react';
import { useHistory, generatePath } from 'react-router-dom';
import styled from '@emotion/styled';
import { Flex, H3, Text } from '@opendoor/bricks/core';
import { CakeLayout } from 'sellDirect/components/legacy/layout/CakeLayout';
import {
  CakeContentCard,
  CakeContentWrapper,
} from 'sellDirect/components/legacy/layout/CakeContentWrapper';
import { AGENT_ACQ_EMAIL, NAVIGATION_HEIGHT } from 'shared/helpers/globals';
import { LoadingPage } from 'platform/components/LoadingPage';
import { useOfferContext } from 'sellDirect/OfferContext';
import { getHubOverviewRoute } from 'shared/helpers/routes';
import { SSEAContextProvider } from 'sellDirect/SSEAContext';
import { ExteriorAssessmentFAQs } from 'sellDirect/components/legacy/sellerDash/exteriorAssessment/ExteriorAssessmentFAQs';
import { ScheduleHeader } from 'sellDirect/components/legacy/sellerDash/exteriorAssessment/ScheduleHeader';
import { ScheduleSection } from 'sellDirect/components/legacy/sellerDash/exteriorAssessment/ScheduleSection';
import { useQualificationContext } from 'shared/helpers/QualificationContext';
import { useExperimentationContext } from 'sellDirect/ExperimentationContext';
import { useSupportContacts } from 'sellDirect/helpers/useSupportContacts';

const TITLE = 'Your exterior assessment';
const AGENT_TITLE = "Your client's exterior assessment";
const IN_PERSON_TITLE = 'Your home assessment';
const IN_PERSON_AGENT_TITLE = "Your client's home assessment";

const getTitleAndSubtitle = (
  shouldSeeAgentExperience: boolean,
  epContactPhoneOrEmail: string,
  isInPersonSseaEnabled: boolean,
) => {
  const IN_PERSON_SUBTITLE = `Select a time when you know you’ll be home. If you can’t be home but can provide another way for us to access the home, reach out to your Opendoor Expert at ${epContactPhoneOrEmail}.`;

  const IN_PERSON_AGENT_SUBTITLE = `Select AM or PM based on when your client will be home. If your client can’t be home but can provide another way for us to access the home, reach out to your Opendoor Expert at  ${epContactPhoneOrEmail}.`;

  let title = shouldSeeAgentExperience ? AGENT_TITLE : TITLE;
  let subtitle = '';

  if (isInPersonSseaEnabled) {
    title = shouldSeeAgentExperience ? IN_PERSON_AGENT_TITLE : IN_PERSON_TITLE;
    subtitle = shouldSeeAgentExperience ? IN_PERSON_AGENT_SUBTITLE : IN_PERSON_SUBTITLE;
  }
  return { title, subtitle };
};

/*
  The partner identifiers listed map to the partnerIdentifier field on a partner referral. They're all
  expected to always map to a single customer profile. In cases where the partnerIdentifier is in 
  this list, we want to hide any phone number inputs to avoid confusion for the many agents that
  are sharing this profile. 

  This data is hard-coded because there isn't any metadata in the partnership service that distinguishes between
  partners with these shared customer profiles versus other partners. If more use cases come up where we would want
  to distinguish between these kinds of partners, we should consider this being configured in the partnership
  service.

  You can learn more details in this Slack thread: https://opendoor.slack.com/archives/C02E67KT3E1/p1652381885044429
*/
const partnersWithSharedCustomerProfiles = [
  'mark_spain_brokerage', // Mark Spain Brokerage
  'kw_westonepros', // Keller Williams West One Pros
];

/** TODO: Add documentation */
export const ExteriorAssessmentSelfScheduling: React.FC = () => {
  const history = useHistory();
  const { agentReferralPartnerIdentifier, addressUUID } = useQualificationContext();
  const { offer, shouldSeeAgentExperience } = useOfferContext();
  const {
    assertions: { isInPersonSseaEnabled },
  } = useExperimentationContext();
  const { experiencePartner: experiencePartnerContact, CXA_EMAIL = '' } = useSupportContacts();
  const contactEmail = shouldSeeAgentExperience ? AGENT_ACQ_EMAIL : CXA_EMAIL;
  const epContactPhoneOrEmail = experiencePartnerContact?.phoneNumber ?? contactEmail;

  if (offer && offer.isExpired) {
    history.push(
      generatePath(getHubOverviewRoute(), {
        addressUUID,
      }),
    );
    return <LoadingPage from="SSEASchedulingPage" reason="OfferExpired" />;
  }

  const StyledCakeContentCard = styled(CakeContentCard)`
    border-bottom: none;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    &:after {
      border-bottom: none;
    }
  `;

  const address = isInPersonSseaEnabled ? offer?.address : undefined;

  const { title, subtitle } = getTitleAndSubtitle(
    shouldSeeAgentExperience,
    epContactPhoneOrEmail,
    isInPersonSseaEnabled,
  );

  const shouldHidePhoneNumberContent =
    !!agentReferralPartnerIdentifier &&
    partnersWithSharedCustomerProfiles.includes(agentReferralPartnerIdentifier);

  const renderContent = () => (
    <Flex flexDirection="column" mb={10} data-testid="legacy-ea-page">
      <CakeContentWrapper boxShadow="none" minHeight={`calc(100vh - ${NAVIGATION_HEIGHT})`}>
        <ScheduleHeader
          isInPersonSseaEnabled={isInPersonSseaEnabled}
          shouldSeeAgentExperience={shouldSeeAgentExperience}
        />
        {address && (
          <CakeContentCard fullWidth isInlay>
            <Flex width="100%" bg="neutrals30" justifyContent="center">
              <Text fontSize="s00" lineHeight={1} color="neutrals90" fontWeight="semibold" my={3}>
                {address.street}
              </Text>
            </Flex>
          </CakeContentCard>
        )}
        <StyledCakeContentCard>
          <H3 mt={9}>{title}</H3>
          {isInPersonSseaEnabled && (
            <Text py={7} size="s1">
              {subtitle}
            </Text>
          )}
        </StyledCakeContentCard>
        <StyledCakeContentCard data-testid="ea-schedule-section">
          <ScheduleSection
            shouldSeeAgentExperience={shouldSeeAgentExperience}
            shouldHidePhoneNumberContent={shouldHidePhoneNumberContent}
            isInPersonSseaEnabled={isInPersonSseaEnabled}
          />
        </StyledCakeContentCard>
        <CakeContentCard forceBorder isInlay>
          <ExteriorAssessmentFAQs
            isInPersonSseaEnabled={isInPersonSseaEnabled}
            shouldSeeAgentExperience={shouldSeeAgentExperience}
            epContactPhoneOrEmail={epContactPhoneOrEmail}
          />
        </CakeContentCard>
      </CakeContentWrapper>
    </Flex>
  );

  return (
    <SSEAContextProvider>
      <CakeLayout hideNavBar renderContent={renderContent} />;
    </SSEAContextProvider>
  );
};
